// VerifySupplierPO.jsx - Enhanced with Price Update Functionality
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    ArrowLeft, IndianRupee, Building, Calendar, FileText,
    CheckCircle, XCircle, Clock, Search, RefreshCw,
    ShoppingCart, User, MapPin, Hash, Target,
    Truck, Package, BadgeCheck, X, Eye, FileCheck,
    Timer, UserCheck, CircleIndianRupee, FileBarChart,
    FileX, ArrowUpCircle, Percent, Calculator,
    CreditCard, FileSpreadsheet, Clipboard,
    Landmark, CheckSquare, ArrowRightLeft, Layers, ExternalLink,
    AlertTriangle, Download, ClipboardList, Receipt, Edit3,
    BarChart3, History, MousePointer, Info, ChevronDown, ChevronUp,
    ChevronRight, ChevronLeft, Maximize2, Minimize2, Square, CheckBox,
    DollarSign, TrendingDown, RefreshCcw, AlertOctagon
} from 'lucide-react';

// ✅ SUPPLIER PO SLICE IMPORTS
import {
    // Async Thunks
    fetchVerificationSupplierPOs,
    fetchSupplierPOByNo,
    approveSupplierPO,

    // Data Selectors
    selectVerificationSupplierPOs,
    selectVerificationSupplierPOsArray,
    selectSupplierPOData,
    selectSelectedRoleId,

    // Loading Selectors
    selectVerificationSupplierPOsLoading,
    selectSupplierPODataLoading,
    selectApproveSupplierPOLoading,

    // Error Selectors
    selectVerificationSupplierPOsError,

    // Actions
    setSelectedRoleId,
    setSelectedUserId,
    setSelectedCCType,
    setSelectedPONo,
    setSelectedIndentNo,
    setSelectedSupplierCode,
    resetSupplierPOData
} from '../../slices/supplierPOSlice/supplierPOSlice';

// ✅ PURCHASE HELPER SLICE IMPORTS
import {
    fetchRemarks,
    fetchPreviousPODetails,
    selectRemarks,
    selectPreviousPODetails,
    selectRemarksLoading,
    selectPreviousPODetailsLoading,
    setSelectedTrno,
    setSelectedMOID,
    setSelectedItemCode
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

// ✅ APPROVAL SLICE IMPORTS (Generic/Reusable)
import {
    // Async Thunks
    fetchStatusList,

    // Data Selectors
    selectStatusList,
    selectAvailableActions,
    selectEnabledActions,
    selectHasActions,

    // Loading Selectors  
    selectStatusListLoading,

    // Error Selectors
    selectStatusListError,

    // Actions
    resetApprovalData,
    clearError as clearApprovalError
} from '../../slices/CommonSlice/getStatusSlice';

// ✅ IMPORT AMOUNT HELPER
import {
    convertAmountToWords,
    formatIndianCurrency,
    getAmountDisplay,
    isValidAmount
} from '../../utilities/amountToTextHelper';

// ✅ REUSABLE INBOX COMPONENTS
import InboxHeader from '../../components/Inbox/InboxHeader';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import AttachmentModal from '../../components/Inbox/AttachmentModal';

// ✅ S3 CONFIG
import { buildSupplierPOUrl, isImageFile, isPdfFile, getFileName } from '../../config/s3Config';

const VerifySupplierPO = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();

    // ✅ SUPPLIER PO STATE
    const verificationPOs = useSelector(selectVerificationSupplierPOsArray);
    const selectedPOData = useSelector(selectSupplierPOData);
    const posLoading = useSelector(selectVerificationSupplierPOsLoading);
    const poDataLoading = useSelector(selectSupplierPODataLoading);
    const approvalLoading = useSelector(selectApproveSupplierPOLoading);
    const posError = useSelector(selectVerificationSupplierPOsError);
    const selectedRoleId = useSelector(selectSelectedRoleId);

    // ✅ PURCHASE HELPER STATE
    const remarks = useSelector(selectRemarks);
    const previousPODetails = useSelector(selectPreviousPODetails);
    const remarksLoading = useSelector(selectRemarksLoading);
    const previousPOLoading = useSelector(selectPreviousPODetailsLoading);

    // ✅ APPROVAL STATE (Generic/Reusable)
    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const { userData, userDetails } = useSelector((state) => state.auth);

    // ✅ GET USER ID AND ROLE ID FROM AUTH STATE
    const roleId = userData?.roleId || userData?.RID;
    const uid = userData?.UID || userData?.uid;

    // Legacy VerifySupplierPOView (MVC controller): 'Exist' only when the PO's PriceChangeRole is set and is
    // the logged-in role. spApproveSupplierPO updates master standard prices only when this is 'Exist'.
    const priceChangeRole = Number(selectedPOData?.PriceChangeRole || 0);
    const priceChangeAccess = priceChangeRole !== 0 && priceChangeRole === Number(roleId) ? 'Exist' : 'NotExist';

    // ðŸ" COMPREHENSIVE DEBUG LOGGING
    console.log('ðŸ" Auth State Debug:', {
        userData,
        roleId,
        uid,
        'userData.roleId': userData?.roleId,
        'userData.RID': userData?.RID,
        'userData.UID': userData?.UID,
        'userData.uid': userData?.uid,
        'notificationData': notificationData
    });

    // ✅ LOCAL STATE
    const [selectedPO, setSelectedPO] = useState(null);
    const [verificationComment, setVerificationComment] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [filterVendor, setFilterVendor] = useState('All');
    const [filterCCType, setFilterCCType] = useState('All');

    // ✅ ENHANCED STATE FOR PRICE MANAGEMENT
    const [editablePrices, setEditablePrices] = useState({});
    const [showPreviousDetails, setShowPreviousDetails] = useState(null);
    const [selectedItemCodeLocal, setSelectedItemCodeLocal] = useState(null);
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);

    // ✅ UI Enhancement States
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [checkedItems, setCheckedItems] = useState({});
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);

    // ✅ QCS ATTACHMENT MODAL STATE
    const [showAttachmentModal, setShowAttachmentModal] = useState(false);
    const [attachmentUrl, setAttachmentUrl] = useState('');

    // ✅ NEW: Price Update Management States
    const [priceUpdateModal, setPriceUpdateModal] = useState({
        show: false,
        itemCode: null,
        itemName: '',
        basicPrice: 0,
        newBasicPrice: 0,
        onConfirm: null,
        onCancel: null
    });
    const [updatedStandardPrices, setUpdatedStandardPrices] = useState({});
    const [recentPriceChanges, setRecentPriceChanges] = useState({});

    // ✅ EXTRACT NOTIFICATION DATA (FOR UI DISPLAY ONLY)
    const {
        InboxTitle,
        ModuleDisplayName
    } = notificationData || {};

    // Inbox has separate PCC / NPCC entries ('/Purchase/VerifySupplierPO?CCType=NPCC',
    // 'Supplier PO Creation(NPCC)'); legacy VerifySupplierPOGrid passes that CCType to the queue SP.
    const ccType = /cctype=npcc/i.test(notificationData?.NavigationPath || '') ||
        /\(npcc\)/i.test(notificationData?.ModuleCategory || '') ? 'NPCC' : 'PCC';

    // Load the queue on open and whenever the inbox entry (CC type) changes.
    useEffect(() => {
        if (roleId && uid) {
            dispatch(setSelectedRoleId(roleId));
            dispatch(setSelectedUserId(uid));
            dispatch(setSelectedCCType(ccType));
            dispatch(fetchVerificationSupplierPOs({ roleId: roleId, userId: uid, ccType }));
        }
    }, [roleId, uid, ccType, dispatch]);

    // ✅ ADD CONSOLE LOGGING FOR DATA STATE CHANGES
    useEffect(() => {
        console.log('ðŸ"Š Data State Update:', {
            'POs Loading': posLoading,
            'POs Error': posError,
            'POs Count': verificationPOs.length,
            'PO Data Loading': poDataLoading,
            'Selected PO Data': selectedPOData ? 'Loaded' : 'Not Loaded',
            'Selected PO': selectedPO?.PONo || 'None'
        });
    }, [posLoading, posError, verificationPOs.length, poDataLoading, selectedPOData, selectedPO]);

    useEffect(() => {
        if (selectedPOData?.MOID && roleId) {
            const statusParams = {
                MOID: selectedPOData.MOID,
                ROID: roleId,
                ChkAmt: calculatePOTotalAmount(selectedPOData) || 0
            };
            console.log('ðŸ"Š Fetching Status List with params:', statusParams);
            dispatch(fetchStatusList(statusParams));
        } else {
            console.log('âŒ Skipping Status List fetch:', {
                'MOID missing': !selectedPOData?.MOID,
                'roleId missing': !roleId,
                'selectedPOData': selectedPOData
            });
        }
    }, [selectedPOData?.MOID, roleId, dispatch]);

    // ✅ FETCH REMARKS WHEN PO IS SELECTED
    useEffect(() => {
        if (selectedPO?.PONo && selectedPOData?.MOID) {
            dispatch(setSelectedTrno(selectedPO.PONo));
            dispatch(setSelectedMOID(selectedPOData.MOID));
            dispatch(fetchRemarks({ trno: selectedPO.PONo, moid: selectedPOData.MOID }));
        }
    }, [selectedPO?.PONo, selectedPOData?.MOID, dispatch]);

    // ✅ NEW: Auto-collapse left panel when PO is selected
    useEffect(() => {
        if (selectedPO) {
            setIsLeftPanelCollapsed(true);
        }
    }, [selectedPO]);

    // ✅ NEW: Initialize checked items and check for recent price changes when PO data is loaded
    useEffect(() => {
        if (selectedPOData?.PODataList) {
            const initialCheckedState = {};
            const priceChanges = {};

            selectedPOData.PODataList.forEach(item => {
                initialCheckedState[item.itemcode] = false;

                // ✅ NEW: Check for recent price changes
                const basicPrice = parseFloat(item.basicprice || 0);
                const itemNewPrice = parseFloat(item.ItemNewPrice || 0);

                if (basicPrice !== itemNewPrice && itemNewPrice > 0) {
                    priceChanges[item.itemcode] = {
                        basicPrice,
                        itemNewPrice,
                        hasRecentChange: true
                    };
                }
            });

            setCheckedItems(initialCheckedState);
            setRecentPriceChanges(priceChanges);

            // Show notification if there are recent price changes
            const changedItemsCount = Object.keys(priceChanges).length;
            if (changedItemsCount > 0) {
                toast.info(
                    `⚠️ ${changedItemsCount} item(s) have recent price changes. Check item details for updated prices.`,
                    { autoClose: 8000 }
                );
            }
        }
    }, [selectedPOData]);

    // ✅ NEW: Price Comparison and Update Functions
    const checkPriceDifference = (item) => {
        const basicPrice = parseFloat(item.basicprice || 0);
        const newBasicPrice = parseFloat(item.NewBasicprice || 0);
        return basicPrice !== newBasicPrice;
    };

    const hasRecentPriceChange = (itemCode) => {
        return recentPriceChanges[itemCode]?.hasRecentChange || false;
    };

    // Purchase price as currently entered in the grid (legacy reads the editable input, not the saved value).
    const getCurrentPurchasePrice = (item) => parseFloat(editablePrices[item.itemcode] ?? item.NewBasicprice ?? 0);

    const showPriceUpdateConfirmation = (item, onConfirm, onCancel) => {
        setPriceUpdateModal({
            show: true,
            itemCode: item.itemcode,
            itemName: item.itemname,
            basicPrice: parseFloat(item.basicprice || 0),
            newBasicPrice: getCurrentPurchasePrice(item),
            onConfirm,
            onCancel
        });
    };

    const handlePriceUpdate = (itemCode, shouldUpdate) => {
        if (shouldUpdate) {
            const item = selectedPOData.PODataList.find(i => i.itemcode === itemCode);
            if (item) {
                const newPrice = getCurrentPurchasePrice(item);

                // Update the standard price and ItemNewPrice
                setUpdatedStandardPrices(prev => ({
                    ...prev,
                    [itemCode]: {
                        oldBasicPrice: parseFloat(item.basicprice),
                        newBasicPrice: newPrice,
                        updatedAt: new Date().toISOString()
                    }
                }));

                console.log('ðŸ’° Price Update Applied:', {
                    itemCode,
                    oldBasicPrice: item.basicprice,
                    newBasicPrice: newPrice,
                    itemName: item.itemname
                });

                toast.success(`✅ Standard price updated for ${item.itemname} from ₹${formatIndianCurrency(item.basicprice)} to ₹${formatIndianCurrency(newPrice)}`);
            }
        }

        setPriceUpdateModal({ show: false, itemCode: null, itemName: '', basicPrice: 0, newBasicPrice: 0, onConfirm: null, onCancel: null });
    };

    // ✅ HELPER FUNCTIONS
    const getCurrentUser = () => {
        return userData?.userName || userDetails?.userName || 'system';
    };

    const getCurrentRoleName = () => {
        return userDetails?.roleName || userData?.roleName ||
            notificationData?.InboxTitle ||
            notificationData?.ModuleDisplayName ||
            'PO Verifier';
    };

    const formatApprovalComment = (roleName, userName, comment) => {
        return `${roleName} : ${userName} : ${comment}`;
    };

    const updateRemarksHistory = (existingRemarks, newRoleName, newUserName, newComment) => {
        const formattedNewComment = formatApprovalComment(newRoleName, newUserName, newComment);

        if (!existingRemarks || existingRemarks.trim() === '') {
            return formattedNewComment;
        }

        return `${existingRemarks.trim()}||${formattedNewComment}`;
    };

    // Line amount for an item: recompute from the live-edited price if the verifier
    // changed it, otherwise trust the already-correct "Amount" from the PO (POAmount
    // from the retrieval API is always 0 — it's not a usable source for this).
    const getItemBasicAmount = (item) => {
        const editedPrice = editablePrices[item.itemcode];
        return editedPrice != null
            ? parseFloat(editedPrice || 0) * parseFloat(item.quantity || 0)
            : parseFloat(item.Amount || 0);
    };

    // Basic value only — no tax. spApproveSupplierPO never takes tax as input; it
    // computes and writes CGST/SGST/IGST itself during Approve based on vendor/CC
    // state, so the PO amount saved here must stay pre-tax.
    const calculatePOTotalAmount = (poData) => {
        if (!poData?.PODataList || !Array.isArray(poData.PODataList)) return 0;

        return poData.PODataList.reduce((total, item) => total + getItemBasicAmount(item), 0);
    };

    const getPriority = (po) => {
        if (!po) return 'Low';
        const totalAmount = calculatePOTotalAmount(po);
        const poDate = po.PODate ? new Date(po.PODate) : new Date();
        const today = new Date();
        const daysOld = Math.ceil((today - poDate) / (1000 * 60 * 60 * 24));

        if (totalAmount > 100000 || daysOld > 30) return 'High';
        if (totalAmount > 50000 || daysOld > 15) return 'Medium';
        return 'Low';
    };

    const getPriorityColor = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'high': return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200';
            case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200';
            case 'low': return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-200';
        }
    };

    const getActionIcon = (actionType) => {
        const type = actionType.toLowerCase();
        const iconMap = {
            'approve': CheckCircle,
            'verify': CheckCircle,
            'accept': CheckCircle,
            'return': ArrowLeft,
            'send back': ArrowLeft,
            'reject': XCircle,
            'decline': XCircle,
            'forward': ArrowLeft,
            'escalate': ArrowLeft,
            'hold': Clock,
            'pending': Clock
        };
        return iconMap[type] || CheckCircle;
    };

    const getStatusColor = (status) => {
        const statusNum = parseInt(status);
        switch (statusNum) {
            case 1: return 'bg-indigo-100 text-indigo-800 border-indigo-200';
            case 2: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 3: return 'bg-orange-100 text-orange-800 border-orange-200';
            case 4: return 'bg-blue-100 text-blue-900 border-blue-200';
            case 5: return 'bg-indigo-100 text-indigo-800 border-indigo-200';
            case 6: return 'bg-green-100 text-green-800 border-green-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const parseApprovalComments = (remarks) => {
        if (!remarks) return [];

        return remarks.split('||').map(comment => {
            const parts = comment.trim().split(' : ');
            if (parts.length >= 3) {
                return {
                    role: parts[0].trim(),
                    name: parts[1].trim(),
                    comment: parts.slice(2).join(' : ').trim()
                };
            }
            return { role: '', name: '', comment: comment.trim() };
        }).filter(item => item.comment);
    };

    // ✅ ENHANCED FUNCTIONS FOR PRICE MANAGEMENT

    // Price Editing Functions
    const handlePriceEdit = (itemCode, newPrice, originalPrice) => {
        const numericNewPrice = parseFloat(newPrice);
        const numericOriginalPrice = parseFloat(originalPrice);

        if (numericNewPrice > numericOriginalPrice) {
            toast.error('Price can only be reduced, not increased!');
            return;
        }

        if (numericNewPrice < 0) {
            toast.error('Price cannot be negative!');
            return;
        }

        setEditablePrices(prev => ({
            ...prev,
            [itemCode]: numericNewPrice
        }));

        console.log('ðŸ’° Price Updated:', {
            itemCode,
            originalPrice: numericOriginalPrice,
            newPrice: numericNewPrice,
            savings: numericOriginalPrice - numericNewPrice
        });
    };

    // ✅ NEW: Price Color Coding Function
    const getPriceColorClass = (newPrice, standardPrice) => {
        const numericNewPrice = parseFloat(newPrice);
        const numericStandardPrice = parseFloat(standardPrice);

        if (numericNewPrice > numericStandardPrice) {
            return 'bg-red-50 border-red-300 text-red-700 dark:bg-red-900/20 dark:border-red-600 dark:text-red-300';
        } else if (numericNewPrice < numericStandardPrice) {
            return 'bg-green-50 border-green-300 text-green-700 dark:bg-green-900/20 dark:border-green-600 dark:text-green-300';
        }
        return 'bg-white border-gray-300 text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200';
    };

    // Previous Purchase Details Functions
    const handleItemCodeClick = async (itemCode) => {
        setSelectedItemCodeLocal(itemCode);
        dispatch(setSelectedItemCode(itemCode));
        dispatch(fetchPreviousPODetails({ itemcode: itemCode }));
        setShowPreviousDetails(itemCode);

        console.log('ðŸ"Š Fetching Previous Purchase Details for:', itemCode);
    };

    const calculatePriceVariation = (currentPrice, previousPrice) => {
        if (!previousPrice || previousPrice === 0) return { percentage: 0, amount: 0, trend: 'same' };

        const priceDiff = currentPrice - previousPrice;
        const percentage = ((priceDiff / previousPrice) * 100).toFixed(2);

        return {
            percentage: Math.abs(percentage),
            amount: Math.abs(priceDiff),
            trend: priceDiff > 0 ? 'increase' : priceDiff < 0 ? 'decrease' : 'same'
        };
    };

    // ✅ ENHANCED: Item Checkbox Functions with Price Update Logic
    const handleItemCheck = (itemCode, checked) => {
        const item = selectedPOData?.PODataList?.find(i => i.itemcode === itemCode);

        if (!item) {
            console.error('Item not found:', itemCode);
            return;
        }

        if (checked) {
            // Legacy (VerifySupplierPOView row click / .chksingle): with price-change access, any row whose
            // standard price differs from the current purchase price asks "change Standard Price?" —
            // Yes sets standard = purchase price, so this both raises and lowers it.
            if (priceChangeAccess === 'Exist' && parseFloat(item.basicprice || 0) !== getCurrentPurchasePrice(item)) {
                // Show price update confirmation
                showPriceUpdateConfirmation(
                    item,
                    (shouldUpdate) => {
                        // Handle the price update decision
                        handlePriceUpdate(itemCode, shouldUpdate);

                        // Complete the item check
                        setCheckedItems(prev => ({
                            ...prev,
                            [itemCode]: true
                        }));

                        console.log('✅ Item Checked with Price Update Decision:', {
                            itemCode,
                            itemName: item.itemname,
                            priceUpdated: shouldUpdate,
                            basicPrice: item.basicprice,
                            newBasicPrice: item.NewBasicprice
                        });
                    },
                    () => {
                        // User cancelled, don't check the item
                        console.log('âŒ Item Check Cancelled due to Price Update Refusal:', itemCode);
                    }
                );
            } else {
                // No price difference, directly check the item
                setCheckedItems(prev => ({
                    ...prev,
                    [itemCode]: checked
                }));

                console.log('✅ Item Checked (No Price Difference):', {
                    itemCode,
                    itemName: item.itemname,
                    basicPrice: item.basicprice,
                    newBasicPrice: item.NewBasicprice
                });
            }
        } else {
            // Unchecking item
            setCheckedItems(prev => ({
                ...prev,
                [itemCode]: checked
            }));

            console.log('â¬œ Item Unchecked:', itemCode);
        }
    };

    const handleSelectAllItems = (checked) => {
        if (selectedPOData?.PODataList) {
            if (checked) {
                // Check if any items have price differences
                const itemsWithPriceDiff = selectedPOData.PODataList.filter(item => checkPriceDifference(item));

                if (itemsWithPriceDiff.length > 0) {
                    toast.warning(
                        `${itemsWithPriceDiff.length} item(s) have price differences. Please verify each item individually to handle price updates.`,
                        { autoClose: 5000 }
                    );
                    return;
                }
            }

            const newCheckedState = {};
            selectedPOData.PODataList.forEach(item => {
                newCheckedState[item.itemcode] = checked;
            });
            setCheckedItems(newCheckedState);
        }
    };

    const areAllItemsChecked = () => {
        if (!selectedPOData?.PODataList) return false;
        return selectedPOData.PODataList.every(item => checkedItems[item.itemcode]);
    };

    const getCheckedItemsCount = () => {
        return Object.values(checkedItems).filter(Boolean).length;
    };

    // ✅ QCS ATTACHMENT HANDLER
    const handleViewQCS = (filePath) => {
        if (!filePath) {
            toast.error('No QCS document available');
            return;
        }
        const fullUrl = buildSupplierPOUrl(filePath);
        if (!fullUrl) {
            toast.error('Invalid file path');
            return;
        }
        setAttachmentUrl(fullUrl);
        setShowAttachmentModal(true);
    };

    // ✅ EVENT HANDLERS
    const handleBackToInbox = () => {
        if (onNavigate) {
            onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
        }
    };

    const handlePOSelect = async (po) => {
        console.log('ðŸ"„ PO Selected:', {
            'PO No': po.PONo,
            'Indent No': po.IndentNo,
            'Vendor Name': po.VendorName,
            'PO': po
        });

        setSelectedPO(po);
        dispatch(setSelectedPONo(po.PONo));
        dispatch(setSelectedIndentNo(po.IndentNo));
        dispatch(setSelectedSupplierCode(po.VendorCode));

        console.log('ðŸ"¡ Fetching PO Details for:', po.PONo, po.IndentNo);
        dispatch(fetchSupplierPOByNo({ poNo: po.PONo, indentNo: po.IndentNo }));

        // Reset states when new PO is selected
        setEditablePrices({});
        setShowPreviousDetails(null);
        setShowRemarksHistory(false);
        setCheckedItems({});
        setUpdatedStandardPrices({});
        setRecentPriceChanges({});
        setPriceUpdateModal({ show: false, itemCode: null, itemName: '', basicPrice: 0, newBasicPrice: 0, onConfirm: null, onCancel: null });
        setShowAttachmentModal(false);
        setAttachmentUrl('');
    };

    const buildPOApprovalPayload = (actionValue, selectedPO, selectedPOData, verificationComment) => {
        const currentUser = getCurrentUser();
        const currentRoleName = getCurrentRoleName();

        const updatedRemarks = updateRemarksHistory(
            selectedPOData?.ApprovedUser,
            currentRoleName,
            currentUser,
            verificationComment
        );

        // spApproveSupplierPO does NOT read PODataList — it reads these as parallel
        // comma-separated strings (one entry per item, same order, trailing comma,
        // matching the SP's own list-building convention). This is what actually
        // drives "Update PODetails set NewBasicprice=..., Amount=... " per item and
        // "Update SupplierPO set Amount=@NewtotalAmount, POBalance=@NewtotalAmount".
        const items = selectedPOData?.PODataList || [];

        let itemcodes = '';
        let newPurchasePrices = '';
        let itemNewTotals = '';
        let standardPrices = '';
        let newTotalAmt = 0;
        let oldStandardTotalAmt = 0;
        let oldPurchaseTotalAmt = 0;

        items.forEach(item => {
            const quantity = parseFloat(item.quantity || 0);
            const newPurchasePrice = parseFloat(editablePrices[item.itemcode] ?? item.NewBasicprice ?? 0);
            const itemAmount = getItemBasicAmount(item);
            const stdUpdate = updatedStandardPrices[item.itemcode];

            itemcodes        += `${item.itemcode},`;
            newPurchasePrices += `${newPurchasePrice},`;
            itemNewTotals     += `${itemAmount},`;
            // Legacy builds the standard-price list only with price-change access; a raised standard
            // price never exceeds the (possibly reduced) purchase price.
            if (priceChangeAccess === 'Exist') {
                standardPrices += `${stdUpdate ? Math.min(stdUpdate.newBasicPrice, newPurchasePrice) : (item.basicprice || 0)},`;
            }

            newTotalAmt         += itemAmount;
            // Legacy hidden OldAmount column (server value); fall back to standard price × qty.
            oldStandardTotalAmt += item.OldAmount != null ? parseFloat(item.OldAmount || 0) : parseFloat(item.basicprice || 0) * quantity;
            oldPurchaseTotalAmt += parseFloat(item.Amount || 0);
        });

        const payload = {
            PONo: selectedPO.PONo,
            IndentNo: selectedPO.IndentNo,
            ApprovalNote: verificationComment,
            Remarks: updatedRemarks,
            Action: actionValue,
            Roleid: roleId || selectedRoleId,
            Createdby: getCurrentUser(),

            Itemcodes: itemcodes,
            NewPurchasePrices: newPurchasePrices,
            ItemNewTotal: itemNewTotals,
            Newtotalamt: newTotalAmt,
            Oldtotalamt: oldStandardTotalAmt,
            OldPurchasetotalamt: oldPurchaseTotalAmt,

            // Echo back the server-computed authorization flag rather than deriving it
            // here — it gates whether spApproveSupplierPO is allowed to overwrite the
            // master ItemCodes.BasicPrice, which is a different decision than "did the
            // verifier edit a price in this screen."
            PriceChangeAccess: priceChangeAccess,
            Standardprices: standardPrices,

            ItemTermHeadID: selectedPOData?.ItemTermHeadID || 0,
            PreferredRemarks: selectedPOData?.PreferredRemarks || null,
            PredefinedTermsExist: selectedPOData?.PredefinedTermsExist || 'No',
        };

        console.log('ðŸŽ¯ Built PO Approval Payload with Price Updates:', {
            'Action': actionValue,
            'PO No': selectedPO.PONo,
            'Indent No': selectedPO.IndentNo,
            'RoleId Used': roleId || selectedRoleId,
            'UserId Used': uid,
            'Updated Prices': Object.keys(editablePrices).length,
            'Standard Price Updates': Object.keys(updatedStandardPrices).length,
            'Full Payload': payload
        });

        return payload;
    };

    const onActionClick = async (action) => {
        console.log('ðŸŽ¬ Action Click Started:', {
            'Action': action,
            'Selected PO': selectedPO?.PONo,
            'Comment Length': verificationComment.trim().length,
            'All Items Checked': areAllItemsChecked(),
            'Checked Items': getCheckedItemsCount(),
            'Total Items': selectedPOData?.PODataList?.length || 0,
            'Price Updates': Object.keys(updatedStandardPrices).length
        });

        if (!selectedPO) {
            console.log('âŒ No PO selected');
            toast.error('No PO selected');
            return;
        }

        if (!verificationComment || verificationComment.trim() === '') {
            console.log('âŒ No verification comment provided');
            toast.error('Verification comment is mandatory. Please add your comments before proceeding.');
            return;
        }

        // ✅ Check if all items are verified (checked)
        if (!areAllItemsChecked()) {
            console.log('âŒ Not all items checked');
            toast.error(`Please verify all items before proceeding. ${getCheckedItemsCount()}/${selectedPOData?.PODataList?.length || 0} items verified.`);
            return;
        }

        let actionValue = action.value;
        if (!actionValue || actionValue.trim() === '') {
            const typeToValueMap = {
                'approve': 'Approve',
                'verify': 'Verify',
                'reject': 'Reject'
            };
            actionValue = typeToValueMap[action.type.toLowerCase()] || action.type;
        }

        // Legacy ApproveSupplierPO: every purchase price must be > 0 and not above the PO's purchase price.
        const poItems = selectedPOData?.PODataList || [];
        if (poItems.some((item) => !(parseFloat(editablePrices[item.itemcode] ?? item.NewBasicprice) > 0))) {
            toast.error('Enter Valid Purchase Price');
            return;
        }
        if (poItems.some((item) => parseFloat(editablePrices[item.itemcode] ?? item.NewBasicprice) > parseFloat(item.NewBasicprice))) {
            toast.error('Your Are Not Able To Increase Purchase Price');
            return;
        }

        console.log('ðŸŽ¯ Final Action Value:', actionValue);

        try {
            const payload = buildPOApprovalPayload(
                actionValue,
                selectedPO,
                selectedPOData,
                verificationComment
            );

            console.log(`ðŸš€ Dispatching Approval for PO ${actionValue}:`, selectedPO.PONo);

            const result = await dispatch(approveSupplierPO(payload)).unwrap();

            console.log('✅ Approval Result:', result);

            // API returns ResponseResult { Data: <SP status> }; spApproveSupplierPO's success literal is "Submited".
            const status = typeof result === 'string' ? result : result?.Data;
            if (status !== 'Submited' && status !== 'Submitted') {
                toast.error(status || 'Error Occurred', { autoClose: 10000 });
                return;
            }
            toast.success(`✅ ${action.text} completed successfully!`);

            // Show price update summary if any
            const priceUpdateCount = Object.keys(updatedStandardPrices).length;
            if (priceUpdateCount > 0) {
                setTimeout(() => {
                    toast.info(`ðŸ’° ${priceUpdateCount} standard price(s) updated successfully!`, { autoClose: 5000 });
                }, 1000);
            }

            console.log('ðŸ"„ Refreshing data after successful approval...');
            setTimeout(() => {
                dispatch(fetchVerificationSupplierPOs({ roleId: roleId || selectedRoleId, userId: uid, ccType }));
                setSelectedPO(null);
                setVerificationComment('');
                setEditablePrices({});
                setShowPreviousDetails(null);
                setShowRemarksHistory(false);
                setCheckedItems({});
                setUpdatedStandardPrices({});
                setRecentPriceChanges({});
                setIsLeftPanelCollapsed(false);
                dispatch(resetSupplierPOData());
                dispatch(resetApprovalData());
            }, 1000);

        } catch (error) {
            console.error(`âŒ PO ${action.type} Error:`, error);
            let errorMessage = `Failed to ${action.text.toLowerCase()}`;

            if (error && typeof error === 'string') {
                errorMessage = `âŒ ${error}`;
            } else if (error?.message) {
                errorMessage = `âŒ ${error.message}`;
            }

            toast.error(errorMessage, { autoClose: 10000 });
        }
    };

    // ✅ FILTER POs BASED ON SEARCH AND FILTERS
    const filteredPOs = verificationPOs.filter(po => {
        const matchesSearch = po.VendorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            po.PONo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            po.IndentNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            po.CCCode?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesVendor = filterVendor === 'All' || po.VendorName === filterVendor;
        const matchesCCType = filterCCType === 'All' || po.CCType === filterCCType;

        return matchesSearch && matchesVendor && matchesCCType;
    });

    const vendors = [...new Set(verificationPOs.map(po => po.VendorName).filter(Boolean))];
    const ccTypes = [...new Set(verificationPOs.map(po => po.CCType).filter(Boolean))];

    // Collapsed item icon for the left panel
    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-blue-200 bg-gradient-to-br from-blue-100 to-blue-100 dark:from-blue-800/50 dark:to-blue-800/50 flex items-center justify-center">
            <ShoppingCart className="w-4 h-4 text-blue-800 dark:text-blue-400" />
        </div>
    );

    // Card rendering for each PO in the left panel list
    const renderItemCard = (po) => {
        const priority = getPriority(po);
        const totalAmount = calculatePOTotalAmount(po);
        const amountDisplay = getAmountDisplay(totalAmount);
        return (
            <div className="p-4">
                <div className="flex items-center space-x-3 mb-3">
                    <div className="relative">
                        <div className="w-12 h-12 rounded-full border-2 border-blue-200 bg-gradient-to-br from-blue-100 to-blue-100 dark:from-blue-800/50 dark:to-blue-800/50 flex items-center justify-center">
                            <ShoppingCart className="w-5 h-5 text-blue-800 dark:text-blue-400" />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{po.VendorName}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{po.VendorCode}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full border ${getPriorityColor(priority)}`}>{priority}</span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                    <div className="flex items-center justify-between">
                        <span className="flex items-center space-x-1">
                            <Hash className="w-3 h-3" />
                            <span className="truncate">{po.PONo}</span>
                        </span>
                        <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">{po.CCType}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-blue-800 dark:text-blue-400 font-medium">₹{amountDisplay.formatted}</span>
                        <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">{po.CCCode}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-xs">
                            <ClipboardList className="w-3 h-3 inline mr-1" />
                            Indent: {po.IndentNo?.slice(-6) || 'N/A'}
                        </span>
                        <span className="text-gray-500 text-xs">Ref: {po.RefNo || 'N/A'}</span>
                    </div>
                </div>
            </div>
        );
    };

    // Compact single-line row for the "classic" list view — same fields as
    // renderItemCard, laid out horizontally instead of stacked.
    const renderListItem = (po) => {
        const priority = getPriority(po);
        const amountDisplay = getAmountDisplay(calculatePOTotalAmount(po));
        return (
            <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
                <span className="font-semibold text-gray-900 dark:text-white min-w-[160px] truncate">
                    {po.VendorName}
                </span>
                <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[110px]">
                    <Hash className="w-3 h-3" />
                    {po.PONo}
                </span>
                <span className="text-blue-800 dark:text-blue-400 font-medium min-w-[100px]">
                    ₹{amountDisplay.formatted}
                </span>
                <span className="text-gray-500 dark:text-gray-400 min-w-[90px]">
                    Indent: {po.IndentNo?.slice(-6) || 'N/A'}
                </span>
                <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">
                    {po.CCCode}
                </span>
                <span className={`ml-auto px-2 py-0.5 text-xs rounded-full border whitespace-nowrap ${getPriorityColor(priority)}`}>
                    {priority}
                </span>
            </div>
        );
    };

    // Detail content — shared by RightDetailPanel and the LeftPanel popup
    const renderDetailContent = (isPopup = false) => {
        if (poDataLoading) {
            return (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto mb-4"></div>
                    <p className="text-gray-500 dark:text-gray-400">Loading detailed information...</p>
                </div>
            );
        }
        if (!selectedPOData) {
            return (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto mb-4"></div>
                    <p className="text-gray-500 dark:text-gray-400">Loading PO details...</p>
                </div>
            );
        }
        // The API only sets StateStatus per item ('Same' | 'NotSame' | 'NoNeed' — see
        // spGetPOItemswithGST); the header-level GSTExist/StateStatus are never populated.
        // Legacy derived them in its MVC controller from the first item, so do the same here.
        const firstItemStateStatus = selectedPOData.PODataList?.[0]?.StateStatus;
        const gstExist = !!firstItemStateStatus && firstItemStateStatus !== 'NoNeed';
        const sameState = firstItemStateStatus === 'Same';

        // Legacy-style item grid cells: bordered, centered, compact.
        const thCls = 'px-2 py-2 text-[11px] font-semibold text-center text-white border-l border-white/20 first:border-l-0 whitespace-nowrap';
        const tdCls = 'px-2 py-2 text-xs text-center align-middle text-gray-800 dark:text-gray-200 border-r border-gray-300 dark:border-gray-600';

        return (
            <div className="space-y-6">
                {/* PO Header with Addresses */}
                <div className={`p-6 rounded-xl border-2 ${isPopup ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'bg-gradient-to-r from-blue-50 via-blue-50 to-orange-50 dark:from-blue-900/20 dark:via-blue-900/20 dark:to-orange-900/20 border-blue-200 dark:border-blue-700'}`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-4">
                            <div className="relative">
                                <div className="w-16 h-16 rounded-full border-4 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center shadow-lg">
                                    <ShoppingCart className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                                </div>
                                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center">
                                    <CheckCircle className="w-3 h-3 text-white" />
                                </div>
                            </div>
                            <div>
                                <h3 className="font-bold text-xl text-gray-900 dark:text-white">
                                    {selectedPOData.VendorName}
                                </h3>
                                <p className="font-semibold text-lg text-blue-800 dark:text-blue-400">
                                    PO: {selectedPOData.PONo}
                                </p>
                                <div className="flex items-center space-x-2 mt-1">
                                    <span className="px-3 py-1 text-sm rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-opacity-20 dark:border-opacity-50">
                                        {selectedPOData.CCType || 'PO'}
                                    </span>
                                    <span className="px-3 py-1 text-sm rounded-full border bg-blue-100 text-blue-900 border-blue-200 dark:bg-opacity-20 dark:border-opacity-50">
                                        {selectedPOData.PaymentType || 'Direct'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-3xl font-bold text-blue-900 dark:text-blue-300">
                                ₹{formatIndianCurrency(calculatePOTotalAmount(selectedPOData))}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Total Amount</p>
                            <p className="text-xs text-gray-500 dark:text-gray-500">
                                Status: {selectedPOData.Status || 'Pending'}
                            </p>
                            {Object.keys(editablePrices).length > 0 && (
                                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                    Total Savings: ₹{formatIndianCurrency(
                                        selectedPOData.PODataList?.reduce((total, item) => {
                                            const originalAmount = parseFloat(item.QuotedPrice || item.NewBasicprice) * parseFloat(item.quantity || 0);
                                            const newAmount = parseFloat(editablePrices[item.itemcode] || item.NewBasicprice) * parseFloat(item.quantity || 0);
                                            return total + (originalAmount - newAmount);
                                        }, 0) || 0
                                    )}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
                        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                            <span className="text-xs text-blue-800 dark:text-blue-400 block">Indent No</span>
                            <span className="font-medium text-gray-800 dark:text-gray-200">{selectedPOData.IndentNo}</span>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                            <span className="text-xs text-blue-800 dark:text-blue-400 block">PO Date</span>
                            <span className="font-medium text-gray-800 dark:text-gray-200">{selectedPOData.PODate || 'N/A'}</span>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                            <span className="text-xs text-blue-800 dark:text-blue-400 block">Ref No</span>
                            <span className="font-medium text-gray-800 dark:text-gray-200">{selectedPOData.RefNo}</span>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                            <span className="text-xs text-blue-800 dark:text-blue-400 block">Cost Center</span>
                            <span className="font-medium text-gray-800 dark:text-gray-200">{selectedPOData.CCCode}</span>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-600">
                            <span className="text-xs text-blue-800 dark:text-blue-400 block">LC Applicable</span>
                            <span className={`inline-block mt-0.5 px-2 py-0.5 text-xs font-semibold rounded-full border ${selectedPOData.LCApplicable === 'Yes'
                                ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700'
                                : 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600'}`}>
                                {selectedPOData.LCApplicable || 'No'}
                            </span>
                        </div>
                    </div>

                    {/* Delivery and Invoice Addresses */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                            <h5 className="font-semibold text-blue-900 dark:text-blue-300 mb-3 flex items-center">
                                <Receipt className="w-4 h-4 mr-2" />
                                Invoice Address
                            </h5>
                            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                                <p className="font-medium">{selectedPOData.InvAddress1}</p>
                                <p>{selectedPOData.InvAddress2}</p>
                                <p className="text-xs text-gray-500">GST: {selectedPOData.GstNo}</p>
                                <p className="text-xs text-gray-500">Mobile: {selectedPOData.MobileNo}</p>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                            <h5 className="font-semibold text-blue-900 dark:text-blue-300 mb-3 flex items-center">
                                <Truck className="w-4 h-4 mr-2" />
                                Delivery Address
                            </h5>
                            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                                <p className="font-medium">{selectedPOData.SiteAddress1}</p>
                                <p>{selectedPOData.SiteAddress2}</p>
                                <p className="text-xs text-gray-500">Contact: {selectedPOData.Contact}</p>
                                <p className="text-xs text-gray-500">Mobile: {selectedPOData.SiteMobileNo}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vendor Information */}
                {selectedPOData.VendorAddress && (
                    <div className={`p-5 rounded-xl border ${isPopup ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-700'}`}>
                        <h4 className={`font-semibold mb-4 flex items-center ${isPopup ? 'text-gray-800 dark:text-gray-200' : 'text-green-800 dark:text-green-200'}`}>
                            <Building className="w-5 h-5 mr-2" />
                            Vendor Information
                        </h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                <span className="text-xs text-green-600 dark:text-green-400 block">Vendor Address</span>
                                <span className="font-medium text-gray-800 dark:text-gray-200 text-sm">{selectedPOData.VendorAddress}</span>
                            </div>
                            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                <span className="text-xs text-green-600 dark:text-green-400 block">Vendor GST</span>
                                <span className="font-medium text-gray-800 dark:text-gray-200 font-mono">{selectedPOData.VendorGST}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* QCS Document — shown only when FilePath exists and PO total exceeds ₹50,000 */}
                {selectedPOData.FilePath && calculatePOTotalAmount(selectedPOData) > 50000 && (
                    <div className={`p-5 rounded-xl border ${isPopup ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'bg-gradient-to-br from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20 border-blue-200 dark:border-blue-700'}`}>
                        <h4 className={`font-semibold mb-4 flex items-center ${isPopup ? 'text-gray-800 dark:text-gray-200' : 'text-blue-900 dark:text-blue-200'}`}>
                            <FileCheck className="w-5 h-5 mr-2" />
                            QCS Document
                        </h4>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                                        <Download className="w-5 h-5 text-blue-800 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                            Quotation Comparison Sheet
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">
                                            {getFileName(selectedPOData.FilePath)}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleViewQCS(selectedPOData.FilePath)}
                                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-900 to-orange-500 hover:from-blue-950 hover:to-orange-600 text-white text-sm rounded-lg transition-colors shadow-md"
                                >
                                    <Eye className="w-4 h-4" />
                                    <span>View QCS</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Item Details — flat grid laid out like the legacy VerifySupplierPOView table:
                    [search][verify] Item Code | HSN | Item Name | Specification | Units | Quantity |
                    Quoted Price | Standard Price | Purchase Price | Amount | GST% | Remarks, total in the footer */}
                {selectedPOData.PODataList && selectedPOData.PODataList.length > 0 && (
                    <div className={`p-6 rounded-xl border ${isPopup ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'bg-gradient-to-br from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20 border-blue-200 dark:border-blue-700'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <h4 className={`font-semibold flex items-center ${isPopup ? 'text-gray-800 dark:text-gray-200' : 'text-blue-900 dark:text-blue-200'}`}>
                                <FileSpreadsheet className="w-5 h-5 mr-2" />
                                Purchase Order ({selectedPOData.PODataList.length} Items) - Verification Required
                            </h4>
                            <div className="flex items-center space-x-4">
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={areAllItemsChecked()}
                                        onChange={(e) => handleSelectAllItems(e.target.checked)}
                                        className="w-4 h-4 text-blue-800 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-blue-900 dark:text-blue-300">
                                        Select All ({getCheckedItemsCount()}/{selectedPOData.PODataList.length})
                                    </span>
                                </label>
                                {Object.keys(recentPriceChanges).length > 0 && (
                                    <div className="flex items-center space-x-1 text-xs text-orange-600 bg-orange-100 dark:bg-orange-900/50 px-2 py-1 rounded-full">
                                        <AlertOctagon className="w-3 h-3" />
                                        <span>{Object.keys(recentPriceChanges).length} Recent Price Changes</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[980px] border-collapse">
                                    <thead className={isPopup ? 'bg-gray-700' : 'bg-blue-900 dark:bg-blue-950'}>
                                        <tr>
                                            <th className={`${thCls} w-10`}></th>
                                            <th className={`${thCls} w-12`}>Verify</th>
                                            <th className={thCls}>Item Code</th>
                                            <th className={thCls}>HSN</th>
                                            <th className={thCls}>Item Name</th>
                                            <th className={thCls}>Specification</th>
                                            <th className={thCls}>Units</th>
                                            <th className={thCls}>Quantity</th>
                                            <th className={thCls}>Quoted Price</th>
                                            <th className={thCls}>Standard Price</th>
                                            <th className={thCls}>Purchase Price</th>
                                            <th className={thCls}>Amount</th>
                                            {gstExist && (sameState ? (
                                                <>
                                                    <th className={thCls}>CGST%</th>
                                                    <th className={thCls}>SGST%</th>
                                                </>
                                            ) : (
                                                <th className={thCls}>IGST%</th>
                                            ))}
                                            <th className={thCls}>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedPOData.PODataList.map((item, index) => {
                                            const currentEditablePrice = editablePrices[item.itemcode] || item.NewBasicprice;
                                            const quantity = parseFloat(item.quantity || 0);
                                            const basicValue = parseFloat(currentEditablePrice) * quantity;
                                            const isChecked = checkedItems[item.itemcode] || false;
                                            const hasPriceDifference = checkPriceDifference(item);
                                            const hasRecentChange = hasRecentPriceChange(item.itemcode);
                                            const isStandardPriceUpdated = updatedStandardPrices[item.itemcode];

                                            return (
                                                <tr key={index} className={`border-b border-gray-300 dark:border-gray-600 transition-colors ${isChecked ? 'bg-green-100 dark:bg-green-900/30' :
                                                    hasPriceDifference ? 'bg-orange-50 dark:bg-orange-900/20' :
                                                        hasRecentChange ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                                    }`}>
                                                    <td className={tdCls}>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleItemCodeClick(item.itemcode)}
                                                            className="inline-flex items-center justify-center w-7 h-7 rounded bg-cyan-600 hover:bg-cyan-700 text-white transition-colors"
                                                            title="View previous purchase history"
                                                        >
                                                            <Search className="w-3.5 h-3.5" />
                                                        </button>
                                                    </td>
                                                    <td className={tdCls}>
                                                        <div className="flex flex-col items-center gap-0.5">
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={(e) => handleItemCheck(item.itemcode, e.target.checked)}
                                                                className="w-4 h-4 text-blue-900 border-gray-400 rounded focus:ring-blue-500 cursor-pointer"
                                                            />
                                                            {hasPriceDifference && !isChecked && (
                                                                <AlertTriangle className="w-3.5 h-3.5 text-orange-600" title={`Price difference: standard ₹${formatIndianCurrency(item.basicprice)} vs purchase ₹${formatIndianCurrency(item.NewBasicprice)} — you'll be asked whether to update the standard price`} />
                                                            )}
                                                            {hasRecentChange && (
                                                                <TrendingDown className="w-3.5 h-3.5 text-indigo-600" title={`Recent price change: ₹${formatIndianCurrency(recentPriceChanges[item.itemcode]?.itemNewPrice)}`} />
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className={`${tdCls} font-mono`}>{item.itemcode}</td>
                                                    <td className={tdCls}>{item.HSNCode}</td>
                                                    <td className={`${tdCls} min-w-[140px] whitespace-normal break-words`}>{item.itemname}</td>
                                                    <td className={`${tdCls} min-w-[140px] whitespace-normal break-words`}>{item.specification}</td>
                                                    <td className={tdCls}>{item.units}</td>
                                                    <td className={tdCls}>{item.quantity}</td>
                                                    <td className={tdCls}>{formatIndianCurrency(item.QuotedPrice)}</td>
                                                    <td
                                                        className={`${tdCls} ${isStandardPriceUpdated ? 'text-purple-600 dark:text-purple-400 font-semibold' : ''}`}
                                                        title={isStandardPriceUpdated ? `Standard price updated (was ₹${formatIndianCurrency(updatedStandardPrices[item.itemcode].oldBasicPrice)})` : undefined}
                                                    >
                                                        {formatIndianCurrency(isStandardPriceUpdated ? updatedStandardPrices[item.itemcode].newBasicPrice : item.basicprice)}
                                                    </td>
                                                    <td className={tdCls}>
                                                        <input
                                                            type="number"
                                                            value={currentEditablePrice}
                                                            onChange={(e) => handlePriceEdit(item.itemcode, e.target.value, item.NewBasicprice)}
                                                            disabled={isChecked}
                                                            className={`w-24 px-1.5 py-1 text-xs text-right border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all ${isChecked ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed opacity-60' : getPriceColorClass(currentEditablePrice, item.basicprice)}`}
                                                            step="0.01"
                                                            min="0"
                                                            max={item.NewBasicprice}
                                                            title={isChecked ? 'Price locked after verification' : `Current: ₹${formatIndianCurrency(currentEditablePrice)} | Standard: ₹${formatIndianCurrency(item.basicprice)}`}
                                                        />
                                                    </td>
                                                    <td className={`${tdCls} font-semibold`}>{formatIndianCurrency(basicValue)}</td>
                                                    {gstExist && (sameState ? (
                                                        <>
                                                            <td className={tdCls}>{item.CGSTPercent}</td>
                                                            <td className={tdCls}>{item.SGSTPercent}</td>
                                                        </>
                                                    ) : (
                                                        <td className={tdCls}>{item.IGSTPercent}</td>
                                                    ))}
                                                    <td className={`${tdCls} min-w-[120px] whitespace-normal break-words`}>
                                                        {item.ItemRemark || <span className="text-gray-400 dark:text-gray-500">—</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot className={isPopup ? 'bg-gray-100 dark:bg-gray-700' : 'bg-blue-100 dark:bg-blue-900/30'}>
                                        <tr>
                                            <td colSpan={11} className="px-2 py-2 text-xs font-bold text-right text-gray-800 dark:text-gray-100">Total</td>
                                            <td className="px-2 py-2 text-xs font-bold text-center text-gray-900 dark:text-white border-r border-gray-300 dark:border-gray-600">
                                                {formatIndianCurrency(calculatePOTotalAmount(selectedPOData))}
                                            </td>
                                            <td colSpan={(gstExist ? (sameState ? 2 : 1) : 0) + 1}></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {/* Verification progress / savings */}
                            <div className={`px-4 py-3 border-t border-gray-300 dark:border-gray-600 flex items-center gap-x-6 gap-y-1 flex-wrap text-sm ${isPopup ? 'bg-gray-50 dark:bg-gray-700/50' : 'bg-blue-50 dark:bg-blue-900/20'}`}>
                                <div className={`flex items-center space-x-1 ${areAllItemsChecked() ? 'text-green-600' : 'text-orange-600'}`}>
                                    <CheckSquare className="w-4 h-4" />
                                    <span>Verified: {getCheckedItemsCount()}/{selectedPOData.PODataList.length}</span>
                                </div>
                                {Object.keys(editablePrices).length > 0 && (
                                    <p className="text-green-600 dark:text-green-400">
                                        Total Savings: ₹{formatIndianCurrency(
                                            selectedPOData.PODataList.reduce((total, item) => {
                                                const originalPrice = parseFloat(item.QuotedPrice) * parseFloat(item.quantity || 0);
                                                const newPrice = parseFloat(editablePrices[item.itemcode] || item.QuotedPrice) * parseFloat(item.quantity || 0);
                                                return total + (originalPrice - newPrice);
                                            }, 0)
                                        )}
                                    </p>
                                )}
                                {Object.keys(updatedStandardPrices).length > 0 && (
                                    <p className="text-blue-700 dark:text-blue-400">
                                        Price Updates: {Object.keys(updatedStandardPrices).length}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* PO Terms & Remarks */}
                {selectedPOData.Remarks && (
                    <div className={`p-6 rounded-xl border ${isPopup ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 border-orange-200 dark:border-orange-700'}`}>
                        <h4 className={`font-semibold mb-4 flex items-center ${isPopup ? 'text-gray-800 dark:text-gray-200' : 'text-orange-800 dark:text-orange-200'}`}>
                            <Clipboard className="w-5 h-5 mr-2" />
                            PO Terms & Conditions
                        </h4>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                {selectedPOData.Remarks.split('|').map((term, index) => (
                                    <div key={index} className="flex items-start space-x-2">
                                        <CheckCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                                        <span>{term.trim()}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Approval History Toggle */}
                <div className={`p-4 rounded-xl border border-gray-200 dark:border-gray-700 ${isPopup ? 'bg-white dark:bg-gray-800' : 'bg-gradient-to-br from-gray-50 to-indigo-50 dark:from-gray-900/20 dark:to-indigo-900/20'}`}>
                    <button
                        onClick={() => setShowRemarksHistory(!showRemarksHistory)}
                        className="flex items-center justify-between w-full text-left"
                    >
                        <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center">
                            <UserCheck className="w-5 h-5 mr-2" />
                            View Approval History ({remarks.length})
                        </h4>
                        {showRemarksHistory ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                    </button>
                </div>

                {renderRemarksHistory()}

                {/* Verification Comments */}
                <div className={`p-5 rounded-xl border-2 ${isPopup ? 'bg-white dark:bg-gray-800 border-red-200 dark:border-red-700' : 'bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-red-200 dark:border-red-700'}`}>
                    <label className="text-sm font-bold text-red-800 dark:text-red-200 mb-3 flex items-center">
                        <FileText className="w-4 h-4 mr-2" />
                        <span className="text-red-600 dark:text-red-400">*</span> Verification Comments (Mandatory)
                    </label>
                    <p className="text-xs text-red-600 dark:text-red-400 mb-3">
                        Please verify all PO details, item quantities, prices, terms & conditions, and vendor information.
                    </p>
                    <textarea
                        value={verificationComment}
                        onChange={(e) => setVerificationComment(e.target.value)}
                        className={`w-full px-4 py-3 border-2 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-500 transition-all ${verificationComment.trim() === ''
                            ? 'border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                            : 'border-green-400 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                            }`}
                        rows="4"
                        placeholder="Please verify PO amount, item details, quantities, prices, terms & conditions, delivery requirements..."
                        required
                    />
                    {verificationComment.trim() === '' && (
                        <p className="text-xs text-red-500 dark:text-red-400 mt-1 flex items-center">
                            <span className="w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                            Verification comment is required before proceeding
                        </p>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-4">
                    {renderActionButtons()}
                </div>
            </div>
        );
    };

    // ✅ NEW: Price Update Confirmation Modal
    const renderPriceUpdateModal = () => {
        if (!priceUpdateModal.show) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300]">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
                    <div className="flex items-center mb-4">
                        <div className="p-3 bg-orange-100 dark:bg-orange-900/50 rounded-lg mr-3">
                            <DollarSign className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Price Update Confirmation
                        </h3>
                    </div>

                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg mb-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                            {priceUpdateModal.itemName}
                        </h4>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Current Standard Price:</span>
                                <span className="font-medium text-gray-900 dark:text-white">
                                    ₹{formatIndianCurrency(priceUpdateModal.basicPrice)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">New Purchase Price:</span>
                                <span className="font-medium text-green-600 dark:text-green-400">
                                    ₹{formatIndianCurrency(priceUpdateModal.newBasicPrice)}
                                </span>
                            </div>
                            <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Price Difference:</span>
                                    <span className={`font-medium ${priceUpdateModal.newBasicPrice > priceUpdateModal.basicPrice
                                        ? 'text-red-600 dark:text-red-400'
                                        : 'text-green-600 dark:text-green-400'
                                        }`}>
                                        {priceUpdateModal.newBasicPrice > priceUpdateModal.basicPrice ? '+' : '-'}
                                        ₹{formatIndianCurrency(Math.abs(priceUpdateModal.newBasicPrice - priceUpdateModal.basicPrice))}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg mb-6">
                        <p className="text-sm text-indigo-800 dark:text-indigo-200 font-medium mb-2">
                            ðŸ"¢ Do you want to update the standard price as the new price?
                        </p>
                        <p className="text-xs text-indigo-600 dark:text-indigo-400">
                            This will update the item's standard price for future PO approvals to ensure consistency.
                        </p>
                    </div>

                    <div className="flex space-x-3">
                        <button
                            onClick={() => priceUpdateModal.onConfirm?.(true)}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Yes, Update Price
                        </button>
                        <button
                            onClick={() => priceUpdateModal.onConfirm?.(false)}
                            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
                        >
                            <X className="w-4 h-4 mr-2" />
                            No, Keep Current
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ✅ RENDER PREVIOUS PURCHASE DETAILS POPUP
    const renderPreviousDetailsPopup = () => {
        if (!showPreviousDetails) return null;

        if (previousPOLoading) {
            return (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300]">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto mb-4"></div>
                            <p className="text-gray-700 dark:text-gray-300">Loading previous purchase details...</p>
                        </div>
                    </div>
                </div>
            );
        }

        const currentItem = selectedPOData?.PODataList?.find(item => item.itemcode === showPreviousDetails);
        const currentPrice = parseFloat(editablePrices[showPreviousDetails] || currentItem?.NewBasicprice || 0);

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300]">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                            <History className="w-5 h-5 mr-2 text-blue-800" />
                            Previous Purchase History - {showPreviousDetails}
                        </h3>
                        <button
                            onClick={() => setShowPreviousDetails(null)}
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Current Item Info */}
                    {currentItem && (
                        <div className="bg-gradient-to-r from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20 p-4 rounded-lg mb-6">
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Current Purchase</h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Item Name:</span>
                                    <p className="font-medium text-gray-900 dark:text-white">{currentItem.itemname}</p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Current Price:</span>
                                    <p className="font-bold text-green-600">₹{formatIndianCurrency(currentPrice)}</p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Standard Price:</span>
                                    <p className="font-medium text-gray-700 dark:text-gray-300">₹{formatIndianCurrency(currentItem.basicprice)}</p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Quoted Price:</span>
                                    <p className="font-medium text-indigo-600">₹{formatIndianCurrency(currentItem.QuotedPrice)}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Previous Purchase History */}
                    <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900 dark:text-white">Previous Purchases ({previousPODetails.length})</h4>

                        {previousPODetails.length === 0 ? (
                            <div className="text-center py-8">
                                <FileX className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                <p className="text-gray-500">No previous purchase history found for this item</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {previousPODetails.map((detail, index) => {
                                    const previousPrice = parseFloat(detail.BasicPrice || 0);
                                    const variation = calculatePriceVariation(currentPrice, previousPrice);

                                    return (
                                        <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                                            <div className="grid grid-cols-5 gap-4">
                                                <div>
                                                    <span className="text-xs text-gray-500">Date</span>
                                                    <p className="font-medium text-gray-900 dark:text-white">{detail.PODate}</p>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-gray-500">Vendor</span>
                                                    <p className="font-medium text-gray-900 dark:text-white">{detail.VendorName}</p>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-gray-500">CC Code</span>
                                                    <p className="font-medium text-gray-900 dark:text-white">{detail.CCCode}</p>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-gray-500">Previous Price</span>
                                                    <p className="font-bold text-gray-900 dark:text-white">₹{formatIndianCurrency(previousPrice)}</p>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-gray-500">Price Change</span>
                                                    <div className="flex items-center space-x-2">
                                                        {variation.trend === 'increase' && (
                                                            <span className="flex items-center text-red-600">
                                                                <ArrowUpCircle className="w-4 h-4 mr-1" />
                                                                +{variation.percentage}%
                                                            </span>
                                                        )}
                                                        {variation.trend === 'decrease' && (
                                                            <span className="flex items-center text-green-600">
                                                                <ArrowUpCircle className="w-4 h-4 mr-1 rotate-180" />
                                                                -{variation.percentage}%
                                                            </span>
                                                        )}
                                                        {variation.trend === 'same' && (
                                                            <span className="text-gray-500">No Change</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ✅ RENDER REMARKS HISTORY
    const renderRemarksHistory = () => {
        if (!showRemarksHistory) return null;

        return (
            <div className="bg-gradient-to-br from-gray-50 to-indigo-50 dark:from-gray-900/20 dark:to-indigo-900/20 p-6 rounded-xl border border-gray-200 dark:border-gray-700 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center">
                        <UserCheck className="w-5 h-5 mr-2" />
                        Approval History ({remarks.length} Actions)
                    </h4>
                    <button
                        onClick={() => setShowRemarksHistory(false)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                    >
                        <ChevronUp className="w-4 h-4" />
                    </button>
                </div>

                {remarksLoading ? (
                    <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-900 mx-auto mb-2"></div>
                        <p className="text-gray-500">Loading remarks...</p>
                    </div>
                ) : remarks.length === 0 ? (
                    <div className="text-center py-4">
                        <FileX className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-gray-500">No approval history found</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {remarks.map((remark, index) => (
                            <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/50 rounded-full flex items-center justify-center">
                                        <User className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <span className="font-semibold text-gray-900 dark:text-gray-100">{remark.ActionBy}</span>
                                            <span className="px-2 py-1 text-xs rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-600">
                                                {remark.ActionRole}
                                            </span>
                                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-600">
                                                {remark.Action}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-700 dark:text-gray-300">{remark.ActionRemarks}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderActionButtons = () => {
        if (statusLoading) {
            return (
                <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-900 mx-auto mb-2"></div>
                    <p className="text-gray-500 text-sm">Loading available actions...</p>
                </div>
            );
        }

        if (statusError) {
            return (
                <div className="text-center py-4">
                    <p className="text-red-500 text-sm">Error loading actions: {statusError}</p>
                    <button
                        onClick={() => {
                            console.log('ðŸ"„ Status List Retry with values:', {
                                MOID: selectedPOData?.MOID,
                                roleId,
                                ChkAmt: calculatePOTotalAmount(selectedPOData) || 0
                            });
                            if (selectedPOData?.MOID && roleId) {
                                dispatch(fetchStatusList({
                                    MOID: selectedPOData.MOID,
                                    ROID: roleId,
                                    ChkAmt: calculatePOTotalAmount(selectedPOData) || 0
                                }));
                            }
                        }}
                        className="mt-2 px-3 py-1 bg-blue-900 text-white text-sm rounded hover:bg-blue-950"
                    >
                        Retry
                    </button>
                </div>
            );
        }

        if (!hasActions) {
            return (
                <div className="text-center py-6">
                    <div className="text-gray-500 mb-2">No actions available for this PO</div>
                </div>
            );
        }

        const filteredActions = enabledActions.filter(action =>
            !['return', 'send back'].includes(action.type.toLowerCase())
        );

        if (filteredActions.length === 0) {
            return (
                <div className="text-center py-6">
                    <div className="text-gray-500 mb-2">No applicable actions available</div>
                    <div className="text-xs text-gray-400">Return actions are hidden for this module</div>
                </div>
            );
        }

        const actionCount = filteredActions.length;
        const gridCols = actionCount === 1 ? 'grid-cols-1' :
            actionCount === 2 ? 'grid-cols-1 md:grid-cols-2' :
                actionCount === 3 ? 'grid-cols-1 md:grid-cols-3' :
                    'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

        const isDisabled = approvalLoading || verificationComment.trim() === '' || !areAllItemsChecked();

        return (
            <div className="space-y-4">
                <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Available Actions ({filteredActions.length})
                    </p>
                    {/* ✅ ENHANCED: Items verification status with price update info */}
                    <div className="flex items-center justify-center space-x-4 mb-4">
                        <div className={`flex items-center space-x-1 text-sm ${areAllItemsChecked() ? 'text-green-600' : 'text-orange-600'}`}>
                            <CheckCircle className={`w-4 h-4 ${areAllItemsChecked() ? 'text-green-600' : 'text-orange-600'}`} />
                            <span>Items Verified: {getCheckedItemsCount()}/{selectedPOData?.PODataList?.length || 0}</span>
                        </div>
                        <div className={`flex items-center space-x-1 text-sm ${verificationComment.trim() ? 'text-green-600' : 'text-orange-600'}`}>
                            <FileText className={`w-4 h-4 ${verificationComment.trim() ? 'text-green-600' : 'text-orange-600'}`} />
                            <span>Comments: {verificationComment.trim() ? 'Added' : 'Required'}</span>
                        </div>
                        {Object.keys(updatedStandardPrices).length > 0 && (
                            <div className="flex items-center space-x-1 text-sm text-indigo-600">
                                <RefreshCcw className="w-4 h-4" />
                                <span>Price Updates: {Object.keys(updatedStandardPrices).length}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className={`grid ${gridCols} gap-4`}>
                    {filteredActions.map((action, index) => {
                        const IconComponent = getActionIcon(action.type);

                        return (
                            <button
                                key={`${action.type}-${index}`}
                                onClick={() => onActionClick(action)}
                                disabled={isDisabled}
                                className={`
                                flex items-center justify-center space-x-2 px-6 py-4 
                                ${action.className} 
                                text-white rounded-lg transition-all 
                                disabled:opacity-50 disabled:cursor-not-allowed 
                                font-medium shadow-lg hover:shadow-xl
                                min-h-[60px]
                            `}
                                title={
                                    verificationComment.trim() === '' ? 'Please add verification comments first' :
                                        !areAllItemsChecked() ? `Please verify all items (${getCheckedItemsCount()}/${selectedPOData?.PODataList?.length || 0} verified)` :
                                            `${action.text} (${action.type}: ${action.value})`
                                }
                            >
                                <IconComponent className="w-5 h-5 flex-shrink-0" />
                                <span className="truncate">
                                    {approvalLoading ? 'Processing...' : action.text}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Supplier PO Verification'} (${verificationPOs.length})`}
                subtitle={ModuleDisplayName}
                itemCount={verificationPOs.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={ShoppingCart}
                badgeText="Purchase Module"
                badgeCount={verificationPOs.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by vendor, PO, indent, CC code...',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value)
                }}
                filters={[
                    {
                        value: filterVendor,
                        onChange: (e) => setFilterVendor(e.target.value),
                        defaultLabel: 'All Vendors',
                        options: vendors
                    },
                    {
                        value: filterCCType,
                        onChange: (e) => setFilterCCType(e.target.value),
                        defaultLabel: 'All CC Types',
                        options: ccTypes
                    }
                ]}
                enableViewToggle
            />

            {/* Main Content */}
            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredPOs,
                    selectedItem: selectedPO,
                    onItemSelect: handlePOSelect,
                    renderItem: renderItemCard,
                    renderListItem: renderListItem,
                    renderCollapsedItem: renderCollapsedItem,
                    loading: posLoading,
                    error: posError ? 'Error loading POs' : null,
                    onRefresh: () => dispatch(fetchVerificationSupplierPOs({ roleId: roleId || selectedRoleId, userId: uid, ccType })),
                    config: {
                        title: 'Pending Verification',
                        icon: Clock,
                        emptyMessage: 'No POs found!',
                        itemKey: 'PONo',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: 'calc(100vh - 300px)',
                        headerGradient: 'from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20',
                    },
                    renderPopupContent: (_item) => renderDetailContent(true),
                    popupConfig: {
                        title: 'PO Verification',
                        icon: ShoppingCart,
                        headerGradient: 'from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20',
                        maxWidth: 'max-w-[80vw]',
                    }
                }}
                right={{
                    selectedItem: selectedPO,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Select a PO',
                        icon: ShoppingCart,
                        selectedTitle: 'PO Verification',
                        emptyTitle: 'No PO Selected',
                        emptyMessage: 'Select a supplier PO from the list to view details and take action.',
                        headerGradient: 'from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20',
                        maxHeight: 'calc(100vh - 200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    }
                }}
            />

            {/* ✅ NEW: Price Update Confirmation Modal */}
            {renderPriceUpdateModal()}

            {/* ✅ Previous Purchase Details Popup */}
            {showPreviousDetails && renderPreviousDetailsPopup()}

            {/* ✅ QCS Attachment Modal */}
            <AttachmentModal
                isOpen={showAttachmentModal}
                onClose={() => { setShowAttachmentModal(false); setAttachmentUrl(''); }}
                fileUrl={attachmentUrl}
                fileName={selectedPOData?.FilePath ? getFileName(selectedPOData.FilePath) : 'QCS Document'}
                title="QCS Document"
                subtitle={`PO: ${selectedPOData?.PONo || ''} — Quotation Comparison Sheet`}
                isImageFile={isImageFile}
                isPdfFile={isPdfFile}
                theme="blue"
            />
        </div>

    );
};

export default VerifySupplierPO;
